import * as Yup from 'yup';
import { format, addDays, isBefore, startOfDay, isEqual, isAfter } from 'date-fns';
import React, { JSX, useState } from 'react';
// import { FormCtx } from '../../Form/types';
import { ElinxInfo, Contact, ElinxType } from './types';
import styles from './ElinxPages.module.scss';
import SelectOptionLocked from '@/components/UI/Select/SelectOptionLocked';
import { EmailField } from '@/components/UI';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/providers/Permissions.provider';
import { allCardBrands } from 'vopay';
import { useDialog } from '@/providers/Dialog.provider';
import AddEmailDialog from './AddEmailDialog/AddEmailDialog';

export type BasicOptions = {
  label: string;
  value: string;
  isVisible?: boolean;
};

import {
  Chip,
  CardCheckbox,
  Tooltip,
  Box,
  Select,
  TextField,
  GoogleAddressField,
  MultiSelect,
  DatepickerSimple,
  Radio,
  InteracWordmark,
  UploadFile,
  MaterialSymbol,
  SelectOption,
  sanitizeToNumeric,
  Timestamp,
  IsTodayDate,
  PaymentMethod,
  ElinxNotificationMethod,
  PaymentMethodType,
  withStyles,
  FieldData,
  FormCtx,
  AccountFundingOption,
  FundingTrigger,
  formattedCurrency,
} from 'vopay';
import {
  Button,
  RecurrencePresetSelect,
  RecurringPaymentCard,
  CustomRecurrenceFields,
  RecurrenceEndsField,
  getOrdinalWeekdayCode,
  ordinalLabel,
} from 'UI';
import useDateLocale from '@/hooks/useDateLocale';
import usePaymentMethodCurrencySupport from '@/hooks/useFields/TransactionCreationFields/usePaymentMethodCurrencySupport';
import {
  applyElinxEndsChange,
  applyElinxRuleChange,
  elinxEndsFromValues,
  elinxRuleFromValues,
  refreshByDayForStartDate,
} from './recurrence.helper';
import {
  getAvailableBalance,
  useFundingOptionField,
} from '@/hooks/useFields/TransactionCreationFields/useFundingOptionField';

const classes = withStyles(styles);

// A one-time (non-recurring) request left on today's date sends immediately — the backend
// validates ScheduleStartDate (must be tomorrow+) and only engages its scheduling path once
// Frequency is present, so both fields must agree on whether "today" counts as scheduled.
export function isOneTimeScheduledForToday(values: Partial<ElinxInfo>): boolean {
  return !values.ScheduleStartDate || IsTodayDate(format(new Date(Number(values.ScheduleStartDate)), 'yyyy-MM-dd'));
}

export function useElinxBasicDetailsRows(
  contacts: { id: string; name: string; currency?: string }[],
  onContactSearch: (val: string) => void,
  showMore: boolean,
  formType: ElinxType,
  country: string,
  // Whether the account has a rail for the currency that is not its own (USD for a Canadian
  // account, CAD for a US one); the domestic currency is always offered.
  isUSDEnabled: boolean,
  isCADEnabled: boolean,
  contactBalance?: number,
  // Pay From is only relevant for ePay (formType 'Pay') — mirrors the Payments page rules: shown
  // only when the account's pre-funding trigger is 'never', hidden otherwise.
  fundingOptions: AccountFundingOption[] = [],
  isFetchingFundingOptions = false,
  fundingTrigger: FundingTrigger = 'never',
): FieldData<ElinxInfo>[] {
  const [isNewContact, setIsNewContact] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.BasicDetails' });
  const { t: tPayFrom } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  // Same namespace RecurrencePresetSelect's own rule-label logic uses, so the Custom Recurrence
  // card's "Monthly on day N" / "Monthly on the Nth weekday" labels match the dropdown's wording.
  const { t: tRecurrence } = useTranslation('translations', { keyPrefix: 'UI.RecurrencePresetSelect' });
  const dateLocale = useDateLocale();
  const { permissions } = usePermissions();

  const payFromRow = useFundingOptionField<ElinxInfo>(fundingOptions, isFetchingFundingOptions, {
    fieldName: 'PayFrom',
    translationPrefix: 'PayFrom',
    labelDefaultValue: 'Pay From',
    showFee: true,
    showSettlementTime: true,
    showBalanceInlineSubtitle: true,
    getSubtitle: (option) => {
      if (option.Source === 'balance') {
        return `${option.Currency} ${formattedCurrency(String(getAvailableBalance(option)))}`;
      }

      return tPayFrom('Fields.PayFrom.fundThenPay', { defaultValue: 'Fund the balance first, then pay' });
    },
    // Recurrence replaced the old IsScheduled checkbox as the schedule toggle — Pay From is only
    // relevant for an immediate (same-day, one-time) payment — a future date is scheduled and
    // there's no scheduled pre-funding, so it pays from the balance. Also hidden when there's no
    // real choice to make — e.g. no default bank account (or credit card) for the selected
    // currency, so fundingOptions collapses to just 'balance'.
    isAdditionallyVisible: (values) =>
      formType === 'Pay' &&
      fundingTrigger === 'never' &&
      (values.Recurrence === 'one-time' || !values.Recurrence) &&
      isOneTimeScheduledForToday(values) &&
      fundingOptions.length > 1,
    isOptionDisabled: (option, values) =>
      option.Source === 'balance' &&
      !!values.Amount &&
      Number(sanitizeToNumeric(values.Amount)) > getAvailableBalance(option),
  });

  function monthlyRepeatOnLabels(refDate: Date): { dayOfMonth: string; ordinalWeekday: string } {
    return {
      dayOfMonth: tRecurrence('Options.monthlyOnDay', {
        defaultValue: `Monthly on day ${refDate.getDate()}`,
        day: refDate.getDate(),
      }),
      ordinalWeekday: tRecurrence('Options.monthly', {
        defaultValue: `Monthly on the ${ordinalLabel(tRecurrence, getOrdinalWeekdayCode(refDate))} ${format(refDate, 'EEEE', { locale: dateLocale })}`,
        ordinal: ordinalLabel(tRecurrence, getOrdinalWeekdayCode(refDate)),
        weekday: format(refDate, 'EEEE', { locale: dateLocale }),
      }),
    };
  }

  // Shared by Recurrence/ScheduleStartDate/Frequency: Pay-only, scheduling is only offered
  // under trigger 'never' (backend never applies pre-funding to a scheduled request). Pay
  // From hides itself once Recurrence leaves 'one-time'.
  function canScheduleElinx(): boolean {
    return (
      !!permissions.CreateScheduledTransactions &&
      formType !== 'Connect' &&
      formType !== 'Flex' &&
      (formType !== 'Pay' || fundingTrigger === 'never')
    );
  }

  const CurrencyOptions: SelectOption<BasicOptions>[] = [
    { label: 'CAD', value: 'CAD', isVisible: country === 'CA' || isCADEnabled },
    { label: 'USD', value: 'USD', isVisible: country !== 'CA' || isUSDEnabled },
  ];

  const SemiMonthlyDateOptions: SelectOption<BasicOptions>[] = [
    {
      label: t('SemiMonthlyFrequencyType.Options.semiMonthFirstAndFifteenth', {
        defaultValue: '1st and 15th Day of the Month',
      }),
      value: 'SemiMonthFirstAndFifteenth',
    },
    {
      label: t('SemiMonthlyFrequencyType.Options.semiMonthFifteenthAndLast', {
        defaultValue: '15th and Last Day of the Month',
      }),
      value: 'SemiMonthFifteenthAndLast',
    },
  ];

  const today = format(new Date(), 'yyyy-MM-dd');
  const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');

  return [
    {
      label: () => t('Contact.label', { defaultValue: 'Contact' }),
      fieldName: 'Contact',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: ElinxInfo) => (values.Contact === 'new' ? undefined : values.Contact),
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e) => {
            if (e.target.value !== 'new') {
              ctx.setFieldValue('PaymentMethodDisplayOptions', '');
            }
          },
        });

        return (
          <Select
            version="2"
            filterable
            onFilter={(e) => {
              e.preventDefault();

              e.debounce(() => {
                if (!e.filter || e.filter.length >= 3) {
                  onContactSearch(e.filter);
                }
              }, 1000);
            }}
            options={contacts
              .filter(
                (contact) =>
                  !!contact.name &&
                  contact.name.split(' ').filter((contactFilter) => !!contactFilter).length > 1 &&
                  (formType !== 'Pay' ||
                    !ctx.values.Currency ||
                    !contact.currency ||
                    contact.currency === ctx.values.Currency),
              )
              .slice(0, 99)}
            optionValue={(o) => o.id}
            renderSelectedValue={(data, { Placeholder }) => {
              if (isNewContact) return 'New Contact';

              if (!data) return <Placeholder />;

              return data.name;
            }}
            renderOption={(o, { Option, SelectedIcon, selected, preventDefault, disabled }) => (
              <Option
                onClick={() => {
                  if (disabled) {
                    preventDefault();
                  } else {
                    setIsNewContact(false);
                    ctx.setFieldValue('ExistingContactName', o.name);
                  }
                }}
              >
                {o.name}
                {selected && <SelectedIcon />}
              </Option>
            )}
            renderDropdownFooter={({ close }) => (
              <Box flx="col">
                <Button
                  iconLeft="add"
                  size="small"
                  onClick={() => {
                    setIsNewContact(true);
                    ctx.setFieldValue('OverrideEmailAddress', '');
                    ctx.setFieldValue('ExistingContactName', '');
                    ctx.setFieldValue('Contact', 'new');
                    ctx.setFieldValue('PaymentMethodDisplayOptions', 'New');
                    close();
                  }}
                >
                  {t('Contact.dropdownButton', { defaultValue: 'Add New Contact' })}
                </Button>
              </Box>
            )}
            placeholder={t('Contact.placeholder', { defaultValue: 'Select Contact' })}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
    {
      label: () => t('Currency.label', { defaultValue: 'Currency' }),
      fieldName: 'Currency',
      getInputState: () => 'required',
      showField: () => formType !== 'Connect',
      serializeField: (values: ElinxInfo) => values.Currency,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <Select
            version="2"
            placeholder={t('Currency.placeholder', { defaultValue: 'Select Currency' })}
            options={CurrencyOptions.filter((option) => option.isVisible)}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('Amount.label', { defaultValue: 'Amount' }),
      fieldName: 'Amount',
      getInputState: () => 'required',
      showField: () => formType !== 'Connect',
      serializeField: (values: ElinxInfo) => sanitizeToNumeric(values.Amount),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            preset="currency"
            placeholder={t('Amount.placeholder', { defaultValue: 'Enter amount' })}
            {...fieldData}
          />
        );
      },

      validate: () =>
        Yup.string()
          .test(
            'amount-validation',
            t('Amount.Validation.greaterThanZero', {
              defaultValue: 'This value should be greater than or equal to $0.01.',
            }),
            (value) => {
              return Number(sanitizeToNumeric(value)) > 0.0;
            },
          )
          .test(
            'balance-validation',
            t('Amount.Validation.exceedsBalance', { defaultValue: 'Amount exceeds the available balance.' }),
            (value) => {
              if (formType !== 'Pay' || contactBalance === undefined) return true;

              return Number(sanitizeToNumeric(value)) <= contactBalance;
            },
          )
          .required(),
    },
    {
      label: () => t('EmailAddress.label', { defaultValue: 'Email Address' }),
      fieldName: 'OverrideEmailAddress',
      getInputState: () => 'required',
      showField: (values) => values.Contact !== 'new',
      serializeField: (values: ElinxInfo) => values.OverrideEmailAddress,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            placeholder={t('EmailAddress.placeholder', { defaultValue: 'Enter your email address' })}
            {...fieldData}
          />
        );
      },
      validate: (ctx) => (ctx.values.Contact !== 'new' ? Yup.string().required() : Yup.string()),
    },
    ...payFromRow,
    {
      label: (ctx: FormCtx<ElinxInfo>) => {
        const isRecurring = ctx.values.Recurrence && ctx.values.Recurrence !== 'one-time';

        if (formType === 'Pay' || formType === 'Flex') {
          return isRecurring
            ? t('ScheduleStartDate.Label.requestedTransactionRecurring', { defaultValue: 'Payment Start Date' })
            : t('ScheduleStartDate.Label.requestedTransaction', { defaultValue: 'Payment Date' });
        }

        return isRecurring
          ? t('ScheduleStartDate.Label.collectionRecurring', { defaultValue: 'Collection Start Date' })
          : t('ScheduleStartDate.Label.collection', { defaultValue: 'Collection Date' });
      },
      fieldName: 'ScheduleStartDate',
      getInputState: () => 'required',
      showField: canScheduleElinx,
      // The backend validates ScheduleStartDate (must be tomorrow+) whenever it's present at all,
      // regardless of Frequency — so a same-day one-time request must omit it entirely to send
      // immediately, same as Frequency below.
      serializeField: (values: ElinxInfo) => {
        if (!values.Recurrence) return undefined;
        if (values.Recurrence === 'one-time' && isOneTimeScheduledForToday(values)) return undefined;

        return format(new Date(Number(values.ScheduleStartDate)), 'yyyy-MM-dd');
      },
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const selectedDate = new Date(Number(e.target.value));
            const endDate = new Date(Number(ctx.values.ScheduleEndDate));

            if (
              isAfter(selectedDate, endDate) ||
              format(selectedDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')
            ) {
              const newDate = addDays(selectedDate, 1).getTime();
              ctx.setFieldValue('ScheduleEndDate', newDate.toString());
            }

            refreshByDayForStartDate(ctx, selectedDate);
          },
        });

        const isOneTime = ctx.values.Recurrence === 'one-time';

        return (
          <DatepickerSimple
            variant="outlineShadow"
            {...fieldData}
            placeholder="YYYY-MM-DD"
            isDisabled={fieldData.disabled}
            // One-time can still send today (immediate) — only a recurring schedule must start
            // at least a day out.
            min={(isOneTime ? new Date() : addDays(new Date(), 1)).getTime() as Timestamp}
            iconPosition="left"
          />
        );
      },
      validate: (ctx: FormCtx<ElinxInfo>) =>
        Yup.string()
          .required()
          .test(
            'start-date-validation',
            t('ScheduleStartDate.Validation.invalidDate', {
              now: tomorrow,
              defaultValue: 'Invalid date. Please choose a date on or after {{now}}',
            }),
            (value: string | undefined) => {
              const dateValue = value ? new Date(Number(value)) : undefined;
              const minDate = ctx.values.Recurrence === 'one-time' ? today : tomorrow;

              return !dateValue || format(dateValue, 'yyyy-MM-dd') >= minDate;
            },
          ),
    },
    {
      label: () => t('Recurrence.label', { defaultValue: 'Recurrence' }),
      fieldName: 'Recurrence',
      getInputState: () => 'required',
      // Recurrence is the sole schedule toggle — a separate "Scheduled Payment" checkbox
      // previously gated this and ScheduleStartDate/Frequency below.
      showField: canScheduleElinx,
      // UI-only — the API has no Recurrence param; it's expressed via NameOfFrequency/Interval/ByDay instead.
      serializeField: () => undefined,
      renderField: ({ ctx, getFormikData }) => {
        const startDate = ctx.values.ScheduleStartDate ? new Date(Number(ctx.values.ScheduleStartDate)) : undefined;
        const { disabled } = getFormikData<string>({});

        return (
          <RecurrencePresetSelect
            name="Recurrence"
            value={elinxRuleFromValues(ctx.values)}
            onChange={(rule) => applyElinxRuleChange(ctx, rule)}
            ends={elinxEndsFromValues(ctx.values)}
            onEndsChange={(ends) => applyElinxEndsChange(ctx, ends)}
            startDate={startDate}
            disabled={disabled}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      // Serializes Frequency (driven by the Recurrence field's onChange above), and doubles as
      // the render slot for the Recurring Payment card (shown for every preset except one-time)
      // — Frequency has no UI of its own otherwise.
      label: () => '',
      fieldName: 'Frequency',
      getInputState: () => 'optional',
      showField: canScheduleElinx,
      // The backend only engages its scheduling path (and requires ScheduleStartDate) once
      // Frequency is present, even as 'single' — so a same-day one-time request omits it
      // entirely (send immediately), but a future-dated one-time request still needs it.
      serializeField: (values: ElinxInfo) => {
        if (!values.Recurrence) return undefined;
        if (values.Recurrence !== 'one-time') return values.Frequency;

        return isOneTimeScheduledForToday(values) ? undefined : 'single';
      },
      renderField: ({ ctx, getFormikData }) => {
        if (!ctx.values.Recurrence || ctx.values.Recurrence === 'one-time') return null;

        const refDate = ctx.values.ScheduleStartDate ? new Date(Number(ctx.values.ScheduleStartDate)) : new Date();
        const rule = elinxRuleFromValues(ctx.values);
        const isCustom = ctx.values.Recurrence === 'custom';
        const isSemiMonthly = ctx.values.NameOfFrequency === 'semi-monthly';
        const { disabled } = getFormikData<string>({});

        // These UI-less sibling fields' errors are surfaced here. Derived from the current
        // value directly (not `ctx.errors`, which can lag a render behind after a fresh edit).
        const semiMonthlyDatesError =
          ctx.touched.SemiMonthlyFrequencyType && !ctx.values.SemiMonthlyFrequencyType
            ? t('SemiMonthlyFrequencyType.Validation.required', { defaultValue: 'This field is required' })
            : null;

        const endDateError =
          ctx.touched.ScheduleEndDate &&
          (!ctx.values.ScheduleEndDate ||
            format(new Date(Number(ctx.values.ScheduleEndDate)), 'yyyy-MM-dd') <= format(refDate, 'yyyy-MM-dd'))
            ? t('ScheduleEndDate.Validation.invalidDate', {
                defaultValue: 'Invalid date. Please choose a date on or after start date',
              })
            : null;

        const endingAfterPaymentsError =
          ctx.touched.EndingAfterPayments && Number(sanitizeToNumeric(ctx.values.EndingAfterPayments) || 0) < 1
            ? t('EndingAfterPayments.Validation.atLeastOne', { defaultValue: 'You must have at least 1 payment' })
            : null;

        const intervalError =
          ctx.touched.Interval && isCustom && Number(ctx.values.Interval || 0) < 1
            ? t('Interval.Validation.atLeastOne', { defaultValue: 'Must be at least 1' })
            : null;

        return (
          <RecurringPaymentCard title={t('RecurringPaymentCard.title', { defaultValue: 'Recurring Payment' })}>
            {isSemiMonthly && (
              <div className={classes('row')}>
                <Box flx="col" ai="fs" gapSize="8px">
                  <span>
                    {t('SemiMonthlyFrequencyType.label', { defaultValue: 'Semi-Monthly Dates' })}{' '}
                    <span className={classes('requiredAsterisk')}>*</span>
                  </span>

                  <Select
                    version="2"
                    name="SemiMonthlyFrequencyType"
                    placeholder={t('SemiMonthlyFrequencyType.placeholder', {
                      defaultValue: 'Select Semi-Monthly Dates',
                    })}
                    options={SemiMonthlyDateOptions}
                    value={ctx.values.SemiMonthlyFrequencyType}
                    disabled={disabled}
                    onChange={(e) => {
                      ctx.setFieldValue('SemiMonthlyFrequencyType', e.target.value);
                      ctx.setFieldTouched('SemiMonthlyFrequencyType', true);
                    }}
                    errorMsg={semiMonthlyDatesError || undefined}
                  />
                </Box>
              </div>
            )}

            {isCustom && (
              <CustomRecurrenceFields
                value={{
                  interval: rule.interval || 1,
                  unit: rule.unit || 'week',
                  weekdays: rule.weekdays || [],
                  monthlyRepeatOn: rule.monthlyRepeatOn || 'dayOfMonth',
                }}
                onChange={(value) => {
                  applyElinxRuleChange(ctx, { preset: 'custom', ...value });
                  ctx.setFieldTouched('Interval', true);
                }}
                monthlyRepeatOnLabels={monthlyRepeatOnLabels(refDate)}
                disabled={disabled}
                intervalError={intervalError}
              />
            )}

            <RecurrenceEndsField
              ends={elinxEndsFromValues(ctx.values)}
              onEndsChange={(ends) => {
                applyElinxEndsChange(ctx, ends);
                ctx.setFieldTouched(ends.mode === 'date' ? 'ScheduleEndDate' : 'EndingAfterPayments', true);
              }}
              minEndDate={addDays(refDate, 1).getTime() as Timestamp}
              disabled={disabled}
              dateError={endDateError}
              occurrencesError={endingAfterPaymentsError}
            />
          </RecurringPaymentCard>
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      // No UI — set via the Recurrence field's preset choice (see the Frequency row's card above).
      label: () => '',
      fieldName: 'NameOfFrequency',
      getInputState: () => 'optional',
      showField: (values: ElinxInfo) => values.Frequency === 'recurring',
      serializeField: (values: ElinxInfo) => values.NameOfFrequency,
      renderField: () => null,
      validate: () => Yup.string().required(),
    },
    {
      // No UI — CustomRecurrenceFields' "Repeat every *" already shows the required marker.
      label: () => '',
      fieldName: 'Interval',
      getInputState: () => 'optional',
      showField: (values: ElinxInfo) =>
        values.Frequency === 'recurring' &&
        ['daily', 'weekly', 'monthly', 'annually'].includes(values.NameOfFrequency || ''),
      serializeField: (values: ElinxInfo) => values.Interval || undefined,
      renderField: () => null,
      // Only Custom lets the user type an interval (every other preset hardcodes it to 1 —
      // see applyElinxRuleChange) — clearing the "Repeat every" field would otherwise submit 0.
      validate: (ctx) =>
        ctx.values.Recurrence === 'custom'
          ? Yup.number()
              .transform((value) => Number(value) || 0)
              .min(1, t('Interval.Validation.atLeastOne', { defaultValue: 'Must be at least 1' }))
              .required()
          : Yup.mixed(),
    },
    {
      // No UI here — the WeekdayToggleGroup inside CustomRecurrenceFields already shows its own
      // "Repeat on *" required marker, so this stays 'optional' to avoid rendering an orphan
      // asterisk with no label/input for this invisible field.
      label: () => '',
      fieldName: 'ByDay',
      getInputState: () => 'optional',
      showField: (values: ElinxInfo) =>
        values.Frequency === 'recurring' && ['weekly', 'monthly'].includes(values.NameOfFrequency || ''),
      serializeField: (values: ElinxInfo) => values.ByDay || undefined,
      renderField: () => null,
      validate: (ctx) =>
        ctx.values.Recurrence === 'custom' && ctx.values.NameOfFrequency === 'weekly'
          ? Yup.string().required()
          : Yup.string(),
    },
    {
      // No UI here — rendered inside the Recurring Payment card (Frequency row) instead.
      label: () => '',
      fieldName: 'SemiMonthlyFrequencyType',
      showField: (values: ElinxInfo) => values.NameOfFrequency === 'semi-monthly',
      getInputState: () => 'optional',
      serializeField: (values: ElinxInfo) => values.SemiMonthlyFrequencyType,
      renderField: () => null,
      validate: () => Yup.string().required(),
    },
    {
      // No UI here — the Recurring Payment card's RecurrenceEndsField drives this value for
      // every preset now, not just Custom.
      label: () => '',
      fieldName: 'Ending',
      getInputState: () => 'optional',
      showField: (values: ElinxInfo) => values.Frequency === 'recurring',
      serializeField: (values: ElinxInfo) => values.Ending,
      renderField: () => null,
      validate: () => Yup.string().required(),
    },
    {
      label: () => '',
      fieldName: 'EndingAfterPayments',
      getInputState: () => 'optional',
      showField: (values: ElinxInfo) => values.Ending === 'Payments' && values.Frequency === 'recurring',
      serializeField: (values: ElinxInfo) => sanitizeToNumeric(values.EndingAfterPayments),
      renderField: () => null,
      validate: () =>
        Yup.number()
          .transform((value) => Number(value) || 0)
          .min(1, t('EndingAfterPayments.Validation.atLeastOne', { defaultValue: 'You must have at least 1 payment' }))
          .required(),
    },
    {
      label: () => '',
      fieldName: 'ScheduleEndDate',
      getInputState: () => 'optional',
      showField: (values: ElinxInfo) => values.Ending === 'End Date' && values.Frequency === 'recurring',
      serializeField: (values: ElinxInfo) => {
        const endDate = new Date(Number(values.ScheduleEndDate));

        return format(endDate, 'yyyy-MM-dd');
      },
      renderField: () => null,
      validate: (ctx: FormCtx<ElinxInfo>) =>
        Yup.string()
          .required()
          .test(
            'end-date-validation',
            t('ScheduleEndDate.Validation.invalidDate', {
              defaultValue: 'Invalid date. Please choose a date on or after start date',
            }),
            (value: string | undefined) => {
              const endDate = value ? new Date(Number(value)) : undefined;
              const startDate = new Date(Number(ctx.values.ScheduleStartDate));
              if (!endDate) return false;

              // Compare calendar days only, not full timestamps.
              return format(endDate, 'yyyy-MM-dd') > format(startDate, 'yyyy-MM-dd');
            },
          ),
    },
    {
      label: () => t('TransactionLabel.label', { defaultValue: 'Transaction Label' }),
      fieldName: 'TransactionLabel',
      getInputState: () => 'optional',
      showField: () => showMore,
      serializeField: (values: ElinxInfo) => values.TransactionLabel,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
    },
    {
      label: () => t('Note.label', { defaultValue: 'Notes' }),
      fieldName: 'Note',
      getInputState: (ctx) => (ctx.values.Frequency === 'recurring' ? 'required' : 'optional'),
      showField: (values: ElinxInfo) => (values.Frequency === 'recurring' ? true : showMore),
      serializeField: (values: ElinxInfo) => values.Note,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: (ctx) => (ctx.values.Frequency === 'recurring' ? Yup.string().required() : Yup.string()),
    },
  ];
}

export function useElinxRowsContactDetails(): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.ContactDetails' });

  const ContactTypeOptions: SelectOption<BasicOptions>[] = [
    { label: t('ContactType.Options.individual', { defaultValue: 'Individual' }), value: 'individual' },
    { label: t('ContactType.Options.business', { defaultValue: 'Business' }), value: 'business' },
  ];

  return [
    {
      label: () => t('ContactType.label', { defaultValue: 'Contact Type' }),
      fieldName: 'ContactType',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.ContactType,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <Select version="2" options={ContactTypeOptions} {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('FirstName.label', { defaultValue: 'First Name' }),
      fieldName: 'FirstName',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Contact === 'new' && values.ContactType === 'individual',
      serializeField: (values: ElinxInfo) => values.FirstName?.replace(/, /g, ' ').replace(/,/g, ''),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            placeholder={t('FirstName.placeholder', { defaultValue: 'John' })}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
    {
      label: () => t('LastName.label', { defaultValue: 'Last Name' }),
      fieldName: 'LastName',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Contact === 'new' && values.ContactType === 'individual',
      serializeField: (values: ElinxInfo) => values.LastName?.replace(/, /g, ' ').replace(/,/g, ''),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            placeholder={t('LastName.placeholder', { defaultValue: 'Doe' })}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
    {
      label: () => t('BusinessName.label', { defaultValue: 'Business Name' }),
      fieldName: 'BusinessName',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Contact === 'new' && values.ContactType === 'business',
      serializeField: (values: ElinxInfo) => values.BusinessName?.replace(/, /g, ' ').replace(/,/g, ''),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            placeholder={t('BusinessName.placeholder', { defaultValue: 'Enter business name' })}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
    {
      label: () => t('EmailAddress.label', { defaultValue: 'Email Address' }),
      fieldName: 'NewEmailAddress',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.NewEmailAddress,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            preset="email"
            placeholder={t('EmailAddress.placeholder', { defaultValue: 'example@email.com' })}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
  ];
}

export function useElinxContactAddressRows(Country: string): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.ContactAddress' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Countries' });

  const CountryOptions = [
    { label: tGlobal('CA'), value: 'CA' },
    { label: tGlobal('USLong'), value: 'US' },
  ].filter((o) => o.value === Country);

  function getRequiredInputState() {
    return Country === 'CA' ? 'optional' : 'required';
  }

  return [
    {
      label: () => t('Address.label', { defaultValue: 'Address' }),
      fieldName: 'AddressLine1',
      getInputState: getRequiredInputState,
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.AddressLine1,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <GoogleAddressField
            variant="outlineShadow"
            ISOCountry={[Country]}
            onGoogleAddressSelect={(addressComponents) => {
              ctx.setFieldValue('AddressLine1', addressComponents.address);
              ctx.setFieldValue('City', addressComponents.city);
              ctx.setFieldValue('Province', addressComponents.province);
              ctx.setFieldValue('PostalCode', addressComponents.postalcode);
              ctx.setFieldValue('Country', addressComponents.country);
              ctx.setFieldTouched('AddressLine1', true);
              ctx.setFieldTouched('City', true);
              ctx.setFieldTouched('Province', true);
              ctx.setFieldTouched('PostalCode', true);
              ctx.setFieldTouched('Country', true);
            }}
            placeholder={t('Address.placeholder', { defaultValue: 'Address Line 1' })}
            {...fieldData}
          />
        );
      },
      validate: () => (Country === 'CA' ? Yup.string() : Yup.string().required()),
    },
    {
      label: () => t('Unit.label', { defaultValue: 'Unit' }),
      fieldName: 'AddressLine2',
      getInputState: () => 'optional',
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.AddressLine2,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            placeholder={t('Unit.placeholder', { defaultValue: 'Unit No.' })}
            {...fieldData}
          />
        );
      },
    },
    {
      label: () => t('City.label', { defaultValue: 'City' }),
      fieldName: 'City',
      getInputState: getRequiredInputState,
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.City,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            placeholder={t('City.placeholder', { defaultValue: 'City' })}
            {...fieldData}
          />
        );
      },
      validate: () => (Country === 'CA' ? Yup.string() : Yup.string().required()),
    },
    {
      label: () => t('Province.label', { defaultValue: 'Province/State' }),
      fieldName: 'Province',
      getInputState: getRequiredInputState,
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.Province,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            placeholder={t('Province.placeholder', { defaultValue: 'Province/State' })}
            {...fieldData}
          />
        );
      },
      validate: () => (Country === 'CA' ? Yup.string() : Yup.string().required()),
    },
    {
      label: () => t('Country.label', { defaultValue: 'Country' }),
      fieldName: 'Country',
      getInputState: getRequiredInputState,
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.Country,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <Select
            version="2"
            placeholder={t('Country.placeholder', { defaultValue: 'Country' })}
            options={CountryOptions}
            {...fieldData}
          />
        );
      },
      validate: () => (Country === 'CA' ? Yup.string() : Yup.string().required()),
    },
    {
      label: () => t('PostalCode.label', { defaultValue: 'Postal Code/Zip Code' }),
      fieldName: 'PostalCode',
      getInputState: getRequiredInputState,
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.PostalCode,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            placeholder={t('PostalCode.placeholder', { defaultValue: 'Postal Code/Zip Code' })}
            {...fieldData}
          />
        );
      },
      validate: () => (Country === 'CA' ? Yup.string() : Yup.string().required()),
    },
  ];
}

export function useElinxPaymentSettingsRows(
  formType: ElinxType,
  country: string,
  contactName: string,
  paymentMethods: PaymentMethod[],
): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.ContactPaymentSettings' });
  const { isSelectionMethodAvailable } = usePaymentMethodCurrencySupport(formType);

  const AccountSelectionMethodOptions = [
    { label: t('AccountSelectionMethod.Options.manual', { defaultValue: 'Manual' }), value: 'manual' },
    { label: t('AccountSelectionMethod.Options.online', { defaultValue: 'Online' }), value: 'online' },
  ];

  type PaymentSelectionMethodOption = {
    label: string;
    value: string;
    featureKey: string;
    visible: boolean;
    renderLabel?: JSX.Element;
  };

  const PaymentSelectionMethodOptions: PaymentSelectionMethodOption[] = [
    {
      label: t('RequestPaymentMethodType.Options.bankAccount', { defaultValue: 'Bank Account' }),
      value: 'bank',
      featureKey: 'Bank Account',
      visible: true,
    },
    {
      label: t('RequestPaymentMethodType.Options.interacETransfer', { defaultValue: 'Interac e-Transfer' }),
      renderLabel: <InteracWordmark type={formType === 'Pay' ? 'eTransfer' : 'moneyRequest'} />,
      value: 'email',
      featureKey: formType === 'Pay' ? 'Interac e-Transfer' : 'Interac e-Transfer Money Request',
      visible: country === 'CA' && formType !== 'Flex' && formType !== 'Connect',
    },
    {
      label: t('RequestPaymentMethodType.Options.email', { defaultValue: 'Email/Phone Number' }),
      value: 'email',
      featureKey: 'Email',
      visible: country === 'CA' && formType === 'Connect',
    },
    {
      label: t('RequestPaymentMethodType.Options.creditCard', { defaultValue: 'Credit Card' }),
      value: 'credit',
      featureKey: 'Credit Card',
      visible: formType !== 'Pay',
    },
    {
      label: t('RequestPaymentMethodType.Options.debitCard', { defaultValue: 'Debit Card' }),
      value: 'debitcard',
      featureKey: 'Debit Card',
      visible: formType === 'Pay' || formType === 'Connect',
    },
    {
      label: t('RequestPaymentMethodType.Options.googlePay', { defaultValue: 'Google Pay' }),
      value: 'googlepay',
      featureKey: 'Google Pay',
      visible: formType === 'Collect' || formType === 'Flex',
    },
    {
      label: t('RequestPaymentMethodType.Options.applePay', { defaultValue: 'Apple Pay' }),
      value: 'applepay',
      featureKey: 'Apple Pay',
      visible: formType === 'Collect' || formType === 'Flex',
    },
    {
      label: t('RequestPaymentMethodType.Options.paypal', { defaultValue: 'PayPal' }),
      value: 'paypal',
      featureKey: 'PayPal',
      visible: formType === 'Pay' || formType === 'Connect',
    },
    {
      label: t('RequestPaymentMethodType.Options.venmo', { defaultValue: 'Venmo' }),
      value: 'venmo',
      featureKey: 'Venmo',
      visible: (formType === 'Pay' || formType === 'Connect') && country === 'US',
    },
    {
      label: t('RequestPaymentMethodType.Options.zelle', { defaultValue: 'Zelle' }),
      value: 'zelle',
      featureKey: 'Zelle',
      visible: (formType === 'Pay' || formType === 'Connect') && country === 'US',
    },
  ];

  type PaymentMethodOption = { label: string; value: string; isComingSoon: boolean };

  const paymentMethodsOptions: PaymentMethodOption[] = [
    {
      label: t('PaymentMethodOptions.Options.new', {
        contactName,
        defaultValue: 'Request New Payment Method(s) from {{contactName}}',
      }),
      value: 'New',
      isComingSoon: false,
    },
    {
      label: t('PaymentMethodOptions.Options.existing', {
        contactName,
        defaultValue: "{{contactName}}'s Existing Payment Method(s)",
      }),
      value: 'Existing',
      isComingSoon: true,
    },
  ];

  type PaymentMethodMultiOption = { label: string; value: string; disabled: boolean };

  // type VerificationMethodOption = {
  //   label: string;
  //   value: string;
  //   featureKey: string;
  //   disabled: boolean;
  //   tooltip?: React.ReactNode;
  // };

  return [
    {
      label: () => t('PaymentMethodOptions.label', { defaultValue: 'Payment Method Options' }),
      fieldName: 'PaymentMethodDisplayOptions',
      getInputState: () => (contactName === '' ? 'disabled' : 'required'),
      showField: () => formType !== 'Connect',
      serializeField: (values: ElinxInfo) => {
        const displayOptions = values.PaymentMethodDisplayOptions;

        return displayOptions !== 'Existing' && displayOptions !== 'New' ? 'Both' : displayOptions;
      },
      tooltip: () => (
        <Tooltip
          information={
            <Box gapSize="14px" flx="col">
              <Box gapSize="4px" flx="col">
                <h3 className={classes('toolTipHeader')}>
                  {t('PaymentMethodOptions.Tooltip.newPaymentMethodTitle', {
                    defaultValue: 'Request New Payment Method(s)',
                  })}
                </h3>

                <div>
                  {t('PaymentMethodOptions.Tooltip.newPaymentMethodDescription', {
                    defaultValue: 'Request contacts to provide new Payment Method(s).',
                  })}
                </div>
              </Box>

              <Box gapSize="4px" flx="col">
                <h3 className={classes('toolTipHeader')}>
                  {t('PaymentMethodOptions.Tooltip.existingPaymentMethodTitle', {
                    defaultValue: 'Existing Payment Method(s)',
                  })}
                </h3>

                <div>
                  {t('PaymentMethodOptions.Tooltip.existingPaymentMethodDescription', {
                    defaultValue: 'Payment Method(s) that the user has already registered',
                  })}
                </div>
              </Box>
            </Box>
          }
          position="top"
          width="394px"
        />
      ),
      renderField: ({ getFormikData, ctx }) => {
        const selectFieldData = getFormikData<string>({});

        const multiSelectFieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',') : []),
        });

        const filteredPaymentMethods = paymentMethods.filter(
          (paymentMethod) =>
            ctx.values.PaymentSelectionMethod?.includes(mapToPaymentSelectionValue(paymentMethod.PaymentMethod)) ||
            !ctx.values.PaymentSelectionMethod ||
            ctx.values.PaymentSelectionMethod === '',
        );

        const paymentMethodsMuiltiOptions: PaymentMethodMultiOption[] = [
          {
            label: t('PaymentMethodOptions.Options.new', {
              contactName,
              defaultValue: 'Request New Payment Method(s) from {{contactName}}',
            }),
            value: 'New',
            disabled: false,
          },
          {
            label: t('PaymentMethodOptions.Options.existing', {
              contactName,
              defaultValue: "{{contactName}}'s Existing Payment Method(s)",
            }),
            value: 'Existing',
            disabled: filteredPaymentMethods.length === 0,
          },
        ];

        return formType === 'Flex' ? (
          <Select
            version="2"
            placeholder={t('PaymentMethodOptions.placeholder', { defaultValue: 'Please Select' })}
            options={paymentMethodsOptions}
            sort={false}
            renderOption={(o, { Option, SelectedIcon, selected, preventDefault }) => (
              <Option
                onClick={() => {
                  if (o.isComingSoon) {
                    preventDefault();
                  }
                }}
              >
                <Box flx="row" ai="center" jc="sb" width="100%">
                  <div className={classes(o.isComingSoon && 'optionDisabled')}> {o.label}</div>

                  {o.isComingSoon && (
                    <Chip label={t('PaymentMethodOptions.comingSoonChip', { defaultValue: 'Coming Soon' })} />
                  )}

                  {selected && <SelectedIcon />}
                </Box>
              </Option>
            )}
            renderDropdownHeader={() => (
              <div className={classes('selectHeader')}>
                {t('PaymentMethodOptions.selectHeader', { defaultValue: 'Select one or more options' })}
              </div>
            )}
            {...selectFieldData}
          />
        ) : (
          <MultiSelect
            placeholder={t('PaymentMethodOptions.placeholder', { defaultValue: 'Please Select' })}
            disableOptionWhen={(option) => option.disabled}
            options={paymentMethodsMuiltiOptions}
            {...multiSelectFieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('ReceiverPhoneNumber.label', { defaultValue: 'Phone Number' }),
      fieldName: 'ReceiverPhoneNumber',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) =>
        (!!values.PaymentMethodDisplayOptions && values.PaymentMethodDisplayOptions !== 'New') ||
        (!!values.PaymentSelectionMethod && values.PaymentSelectionMethod.includes('bank') && country === 'US'),
      serializeField: (values: ElinxInfo) => values.ReceiverPhoneNumber,
      tooltip: () => (
        <Tooltip
          information={
            <div>
              {t('ReceiverPhoneNumber.tooltip', {
                defaultValue:
                  'Changes to the phone number will apply only to this transaction and will not be saved to the contact profile.',
              })}
            </div>
          }
          position="top"
          width="298px"
        />
      ),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('RequestPaymentMethodType.label', { defaultValue: 'Request Payment Method Type' }),
      fieldName: 'PaymentSelectionMethod',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: ElinxInfo) =>
        values.PaymentSelectionMethod?.split(',')
          .filter((paymentMethod) => paymentMethod)
          .join(','),
      tooltip: () => (
        <Tooltip
          information={
            <Box gapSize="4px" flx="col">
              <h3 className={classes('toolTipHeader')}>
                {t('RequestPaymentMethodType.Tooltip.title', { defaultValue: 'Payment Method Type' })}
              </h3>

              <div>
                {t('RequestPaymentMethodType.Tooltip.description', {
                  defaultValue:
                    "This let's you select the type of payment method that you would like to request from your contact.",
                })}
              </div>
            </Box>
          }
          position="top"
          width="394px"
        />
      ),
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',') : []),
        });

        // Filter payment methods based on existing payment methods when "Existing" is selected

        let paymentOptions = (
          ctx.values.Recurrence === 'one-time' || !ctx.values.Recurrence
            ? PaymentSelectionMethodOptions.filter((option) => option.visible)
            : PaymentSelectionMethodOptions.filter((option) => option.visible && option.value === 'bank')
        ).filter((option) => isSelectionMethodAvailable(option.value, ctx.values.Currency, { isRequested: true }));

        // If PaymentMethodDisplayOptions is "Existing", filter by available payment methods
        if (ctx.values.PaymentMethodDisplayOptions === 'Existing') {
          const existingPaymentMethodValues = new Set(
            paymentMethods.map((pm) => mapToPaymentSelectionValue(pm.PaymentMethod)),
          );

          paymentOptions = paymentOptions.filter((option) => existingPaymentMethodValues.has(option.value));
        }

        return (
          <MultiSelect
            placeholder={t('RequestPaymentMethodType.placeholder', { defaultValue: 'Please Select' })}
            options={paymentOptions}
            renderOption={(option, args) => (
              <SelectOptionLocked {...args} featureKey={option.featureKey}>
                {option.renderLabel || option.label}
              </SelectOptionLocked>
            )}
            sort={false}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
    // TODO: Uncomment when verification is needed
    // {
    //   label: () => t('Verify.label'),
    //   fieldName: 'Verify',
    //   getInputState: () => 'optional',
    //   showField: () => true,
    //   serializeField: (values: ElinxInfo) =>
    //     values.Verify?.split(',')
    //       .filter((verificationMethod) => verificationMethod)
    //       .join(','),

    //   renderField: ({ ctx, getFormikData }) => {
    //     const fieldData = getFormikData<string[], string>({
    //       formatValue: (value) => (value ? value.split(',') : []),
    //     });
    //     const PaymentVerificationMethodOptions: VerificationMethodOption[] = [
    //       {
    //         label: t('Verify.Options.bank'),
    //         value: 'bank',
    //         featureKey: 'Bank Account Verification',
    //         disabled:
    //           !ctx.values.PaymentSelectionMethod?.includes('bank') || ctx.values.AccountSelectionMethod === 'online',
    //       },
    //       {
    //         label: t('Verify.Options.interac'),
    //         value: 'interac',
    //         featureKey: 'Interac Verification',
    //         disabled: !ctx.values.PaymentSelectionMethod?.includes('email'),
    //       },
    //     ];
    //     return (
    //       <MultiSelect
    //         placeholder={t('Verify.placeholder')}
    //         options={PaymentVerificationMethodOptions}
    //         renderOption={(option, args) => (
    //           <SelectOptionLocked {...args} featureKey={option.featureKey} disabled={option.disabled}>
    //             {option.label}
    //           </SelectOptionLocked>
    //         )}
    //         sort={false}
    //         {...fieldData}
    //       />
    //     );
    //   },

    //   validate: () => Yup.mixed().notRequired(),
    // },
    {
      label: () => t('AccountSelectionMethod.label', { defaultValue: 'Bank Account Connection Type' }),
      fieldName: 'AccountSelectionMethod',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => !!values.PaymentSelectionMethod?.includes('bank') && formType === 'Connect', // only show when bank account has been selected
      serializeField: (values: ElinxInfo) => {
        const accountValues = values.AccountSelectionMethod?.split(',');

        if (accountValues && accountValues.length > 1) {
          return 'any';
        }

        if (accountValues && accountValues.length === 1) {
          return accountValues[0];
        }

        return '';
      },
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',') : []),
        });

        return (
          <MultiSelect
            placeholder={t('AccountSelectionMethod.placeholder', { defaultValue: 'Please Select' })}
            options={AccountSelectionMethodOptions}
            {...fieldData}
            // TODO: Uncomment when verification is needed
            // onChange={(e) => {

            //   if (e.target.value === 'online' && ctx.values.Verify?.includes('bank')) {
            //     const updatedVerify = ctx.values.Verify.split(',')
            //       .map((v) => v.trim())
            //       .filter((v) => v !== 'bank')
            //       .join(',');

            //     ctx.setFieldValue('Verify', updatedVerify);
            //   }
            //   fieldData.onChange(e);
            // }}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
    // move to the first container for now
    // {
    //   label: () => 'Email Address',
    //   fieldName: 'OverrideEmailAddress',
    //   getInputState: (ctx) => (ctx.values.SendEmail ? 'required' : 'optional'),
    //   showField: (values) => !!values.SendEmail && values.Contact !== 'new',
    //   serializeField: (values: ElinxInfo) => values.OverrideEmailAddress,
    //   renderField: ({ getFormikData }) => {
    //     const fieldData = getFormikData<string>({});

    //     return <TextField variant="outlineShadow" disabled={isReview} placeholder="Enter your name" {...fieldData} />;
    //   },
    //   validate: (ctx) => (ctx.values.SendEmail ? Yup.mixed().required('This value is required.') : Yup.mixed()),
    // },
  ];
}

export function useBankAccountRows(formType: ElinxType, country: string): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.BankAccount' });

  type BankingPaymentRailOption = {
    label: string;
    value: string;
    featureKey: string;
    visible: boolean;
    isRecurringEnabled: boolean;
  };

  const BankingPaymentRailOptions: BankingPaymentRailOption[] = [
    {
      label: t('BankingPaymentRail.Options.eft', { defaultValue: 'Electronic Funds Transfer' }),
      value: 'eft',
      featureKey: formType === 'Pay' ? 'EFTSendEnabled' : 'EFTCollectEnabled',
      visible: country === 'CA',
      isRecurringEnabled: true,
    },
    {
      label: 'VoPay Instant',
      value: 'vopayinstant',
      featureKey: 'RTRWithdrawEnabled',
      visible: country === 'CA' && formType === 'Pay',
      isRecurringEnabled: false,
    },
    {
      label: t('BankingPaymentRail.Options.ach', { defaultValue: 'Automated Clearing House' }),
      value: 'ach',
      featureKey: country === 'US' && formType === 'Pay' ? 'ACHWithdrawEnabled' : 'ACHFundEnabled',
      visible: country === 'US',
      isRecurringEnabled: true,
    },
    {
      label: t('BankingPaymentRail.Options.rtp', { defaultValue: 'Real-Time Payment' }),
      value: 'rtp',
      featureKey: 'RTPWithdrawEnabled',
      visible: country === 'US' && formType === 'Pay',
      isRecurringEnabled: false,
    },
    {
      label: t('BankingPaymentRail.Options.wire', { defaultValue: 'Wire' }),
      value: 'fedwire',
      featureKey: 'WireEnabled',
      visible: country === 'US' && formType === 'Pay',
      isRecurringEnabled: false,
    },
  ];

  const AccountSelectionMethodOptions = [
    { label: t('AccountSelectionMethod.Options.manual', { defaultValue: 'Manual' }), value: 'manual' },
    { label: t('AccountSelectionMethod.Options.online', { defaultValue: 'Online' }), value: 'online' },
  ];

  return [
    {
      label: () => t('BankingPaymentRail.label', { defaultValue: 'Payment Rail' }),
      fieldName: 'BankingPaymentRail',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => !!values.PaymentSelectionMethod?.includes('bank'),
      serializeField: (values: ElinxInfo) => values.BankingPaymentRail,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',') : []),
        });

        // Filter based on visibility and recurring capability
        const filteredOptions =
          ctx.values.Recurrence === 'one-time' || !ctx.values.Recurrence
            ? BankingPaymentRailOptions.filter((option) => option.visible)
            : BankingPaymentRailOptions.filter((option) => option.visible && option.isRecurringEnabled);

        return (
          <MultiSelect
            placeholder={t('BankingPaymentRail.placeholder', { defaultValue: 'Please select' })}
            options={filteredOptions}
            renderOption={(option, args) => (
              <SelectOptionLocked {...args} featureKey={option.featureKey}>
                {option.label}
              </SelectOptionLocked>
            )}
            sort={false}
            {...fieldData}
          />
        );
      },
      validate: (ctx) =>
        ctx.values.PaymentSelectionMethod?.includes('bank') && formType !== 'Connect'
          ? Yup.string().required()
          : Yup.string(),
    },
    {
      label: () => t('AccountSelectionMethod.label', { defaultValue: 'Bank Account Connection Type' }),
      fieldName: 'AccountSelectionMethod',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => !!values.PaymentSelectionMethod?.includes('bank'),
      serializeField: (values: ElinxInfo) => {
        const accountValues = values.AccountSelectionMethod?.split(',');

        if (accountValues && accountValues.length > 1) {
          return 'any';
        }

        if (accountValues && accountValues.length === 1) {
          return accountValues[0];
        }

        return '';
      },
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',') : []),
        });

        return (
          <MultiSelect
            placeholder={t('AccountSelectionMethod.placeholder', { defaultValue: 'Please select' })}
            options={AccountSelectionMethodOptions}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
  ];
}

export function useCardSettingsRows(): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.CardSettings' });

  return [
    {
      label: () => t('acceptedCardBrands.label', { defaultValue: 'Accepted Card Brands' }),
      fieldName: 'AcceptedCardBrands',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => {
        const paymentMethods = values.PaymentSelectionMethod?.split(',') || [];

        return paymentMethods.includes('credit') || paymentMethods.includes('debitcard');
      },
      tooltip: () => (
        <Tooltip
          information={t('acceptedCardBrands.tooltip', {
            defaultValue: 'Select the card brands this contact should be able to provide.',
          })}
          position="top"
          width="394px"
        />
      ),
      serializeField: (values: ElinxInfo) => {
        const selectedBrands = values.AcceptedCardBrands?.split(',').filter((b) => b) || [];

        if (selectedBrands.length === allCardBrands.length) {
          return '';
        }

        return selectedBrands.join(',');
      },
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',').filter((b) => b) : []),
        });

        return (
          <MultiSelect
            placeholder={t('acceptedCardBrands.placeholder', { defaultValue: 'Select Card Brands' })}
            options={allCardBrands.map((brand) => ({
              label: brand.charAt(0).toUpperCase() + brand.slice(1),
              value: brand,
            }))}
            {...fieldData}
          />
        );
      },
      validate: (ctx) => {
        const paymentMethods = ctx.values.PaymentSelectionMethod?.split(',') || [];

        return paymentMethods.includes('credit') || paymentMethods.includes('debitcard')
          ? Yup.string().required()
          : Yup.string();
      },
    },
    {
      label: () => t('cardTypeValidation.label', { defaultValue: 'Card Type Validation' }),
      fieldName: 'CardTypeValidation',
      getInputState: () => 'optional',
      showField: (values: ElinxInfo) => {
        const paymentMethods = values.PaymentSelectionMethod?.split(',') || [];

        return paymentMethods.includes('credit') || paymentMethods.includes('debitcard');
      },
      tooltip: () => (
        <Tooltip
          information={t('cardTypeValidation.tooltip', {
            defaultValue:
              "Validates whether the entered card is debit or credit and blocks cards that don't match the allowed types.",
          })}
          position="top"
          width="394px"
        />
      ),
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<boolean>({
          formatValue: (value) => !!value,
        });

        return (
          <CardCheckbox
            label={t('cardTypeValidation.checkboxLabel', { defaultValue: 'Enable card type validation' })}
            checked={ctx.values.CardTypeValidation || false}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.boolean(),
    },
  ];
}

export function useElinxPartialPaymentRows(formType: ElinxType, isEnabled: boolean): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.PartialPayment' });
  const now = format(new Date(), 'yyyy-MM-dd');

  return [
    {
      label: () => t('MinimumPaymentAccepted.label', { defaultValue: 'Minimum Partial Payment' }),
      fieldName: 'MinimumPaymentAccepted',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('partial'),
      serializeField: (values: ElinxInfo) =>
        formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('partial') && values.MinimumPaymentAccepted
          ? sanitizeToNumeric(String(values.MinimumPaymentAccepted))
          : undefined,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            preset="currency"
            placeholder={t('MinimumPaymentAccepted.placeholder', { defaultValue: 'Enter Minimum Payable Amount' })}
            {...fieldData}
          />
        );
      },
      tooltip: () => (
        <Tooltip
          information={t('MinimumPaymentAccepted.tooltip', {
            defaultValue: 'Specify the minimum amount that you are willing to accept as a partial payment.',
          })}
          position="top"
          width="394px"
        />
      ),
      validate: (ctx) =>
        Yup.string()
          .test(
            'amount-validation',
            t('MinimumPaymentAccepted.Validation.greaterThanZero', {
              defaultValue: 'This value should be greater than or equal to $0.01.',
            }),
            (value) => {
              const amountValidation = Number(sanitizeToNumeric(value)) <= 0.0;

              return !amountValidation;
            },
          )
          .test(
            'amount-validation',
            t('MinimumPaymentAccepted.Validation.lessThanAmount', {
              amount: ctx.values.Amount,
              defaultValue: 'This value should be less than {{amount}}',
            }),
            (value) => {
              const amountValidation = Number(sanitizeToNumeric(value)) >= Number(sanitizeToNumeric(ctx.values.Amount));

              return !amountValidation;
            },
          )
          .required(),
    },
    {
      label: () => (
        <span style={{ opacity: 0 }}>
          {t('ScheduleRemainingAmount.label', { defaultValue: 'Schedule Remaining Amount Mandatory' })}
        </span>
      ),
      fieldName: 'ScheduleRemainingAmount',
      getInputState: () => 'optional',
      showField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('partial'),
      serializeField: (values: ElinxInfo) =>
        formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('partial')
          ? values.ScheduleRemainingAmount
          : undefined,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});

        return (
          <CardCheckbox
            indicator="switch"
            checked={fieldData.value}
            label={
              <Box gapSize="4px">
                {t('ScheduleRemainingAmount.label', { defaultValue: 'Schedule Remaining Amount Mandatory' })}{' '}
                <Tooltip
                  position="top"
                  information={t('ScheduleRemainingAmount.tooltip', {
                    defaultValue:
                      'Enable this option to allow your customers to schedule the remaining balance for a future date.',
                  })}
                />
              </Box>
            }
            {...fieldData}
          />
        );
      },
    },
    {
      label: () => t('RemainderPaymentDate.label', { defaultValue: 'Remaining Amount Date' }),
      fieldName: 'RemainderPaymentDate',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => {
        return (
          !!values.ScheduleRemainingAmount &&
          isEnabled &&
          formType === 'Flex' &&
          !!values.FlexiblePaymentOptions?.includes('partial')
        );
      },
      serializeField: (values: ElinxInfo) => {
        if (!values.ScheduleRemainingAmount) {
          return undefined;
        }

        return formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('partial')
          ? format(new Date(Number(values.RemainderPaymentDate)), 'yyyy-MM-dd')
          : undefined;
      },
      tooltip: () => (
        <Tooltip
          information={t('RemainderPaymentDate.tooltip', {
            defaultValue: 'Set the latest date by which the remaining amount must be paid if scheduling is allowed',
          })}
          position="top"
          width="394px"
        />
      ),
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const selectedDate = new Date(Number(e.target.value));
            const endDate = new Date(Number(ctx.values.ScheduleEndDate));

            if (isBefore(selectedDate, endDate)) {
              const newDate = addDays(selectedDate, 1).getTime();
              ctx.setFieldValue('ScheduleEndDate', newDate.toString());
            }
          },
        });

        return (
          <DatepickerSimple
            variant="outlineShadow"
            min={addDays(new Date(), 1).getTime() as Timestamp}
            iconPosition="left"
            {...fieldData}
            isDisabled={fieldData.disabled}
          />
        );
      },
      validate: () =>
        Yup.string()
          .required()
          .test(
            'start-date-validation',
            t('RemainderPaymentDate.invalidDateValidation', {
              now,
              defaultValue: 'Invalid date. Please choose a date after {{now}}',
            }),
            (value: string | undefined) => {
              if (!value) return false;
              const selectedDate = new Date(Number(value));

              return !isBefore(selectedDate, addDays(new Date(), 1));
            },
          ),
    },
  ];
}

export function useElinxInstallmentPlansRows(formType: ElinxType, isEnabled: boolean): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.InstallmentPlan' });

  const installmentFrequencyOptions: SelectOption<BasicOptions>[] = [
    { label: t('InstallmentFrequency.Options.weekly', { defaultValue: 'Weekly' }), value: 'weekly' },
    { label: t('InstallmentFrequency.Options.biweekly', { defaultValue: 'Bi-Weekly' }), value: 'biweekly' },
    { label: t('InstallmentFrequency.Options.monthly', { defaultValue: 'Monthly' }), value: 'monthly' },
  ];

  return [
    {
      label: () => t('NumberOfInstallments.label', { defaultValue: 'Number of Installments' }),
      fieldName: 'NumberOfInstallments',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('installments'),
      serializeField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('installments')
          ? sanitizeToNumeric(values.NumberOfInstallments)
          : undefined,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            preset="numericWholePositive"
            placeholder={t('NumberOfInstallments.placeholder', { defaultValue: 'Enter Number of Installments' })}
            {...fieldData}
          />
        );
      },
      tooltip: () => {
        return (
          <Tooltip
            information={t('NumberOfInstallments.tooltip', {
              defaultValue: 'The total amount will be divided into this number of installments.',
            })}
            position="top"
            width="394px"
          />
        );
      },
      validate: () =>
        Yup.number()
          .transform((value) => Number(value) || 0)
          .min(
            1,
            t('NumberOfInstallments.atLeastOneValidation', { defaultValue: 'You must have at least 1 installment' }),
          )
          .required(),
    },
    {
      label: () => t('InstallmentFrequency.label', { defaultValue: 'Frequency' }),
      fieldName: 'InstallmentFrequency',
      showField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('installments'),
      getInputState: () => 'required',
      serializeField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('installments')
          ? values.InstallmentFrequency
          : undefined,
      tooltip: () => {
        return (
          <Tooltip
            information={t('InstallmentFrequency.tooltip', {
              defaultValue: 'Each installment will be repeated at this frequency.',
            })}
            position="top"
            width="394px"
          />
        );
      },
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <Select
            version="2"
            placeholder={t('InstallmentFrequency.placeholder', { defaultValue: 'Select Frequency' })}
            options={installmentFrequencyOptions}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
  ];
}

export function useElinxDetailsRows(formType: ElinxType): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.ELinxDetails' });

  return [
    {
      label: () => t('SenderName.label', { defaultValue: 'Sent By' }),
      fieldName: 'SenderName',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.SenderName,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            placeholder={t('SenderName.placeholder', { defaultValue: 'Enter your name' })}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('RecipientMessage.label', { defaultValue: 'Message to Contact' }),
      fieldName: 'RecipientMessage',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.RecipientMessage,
      tooltip: () => (
        <Tooltip
          information={t('RecipientMessage.tooltip', {
            defaultValue: 'This message will be included in the eLinx welcome message sent to the contact.',
          })}
          position="top"
          width="394px"
        />
      ),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            placeholder={t('RecipientMessage.placeholder', { defaultValue: 'Enter Description' })}
            {...fieldData}
          />
        );
      },
    },
    {
      label: () => t('InvoiceNumber.label', { defaultValue: 'Invoice Number' }),
      fieldName: 'InvoiceNumber',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.InvoiceNumber,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
    },
    {
      label: () => t('UploadInvoice.label', { defaultValue: 'Upload Invoice' }),
      fieldName: 'InvoiceFile',
      getInputState: () => 'optional',
      showField: () => formType !== 'Connect',
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<string>({});

        return (
          <UploadFile
            id={fieldData.name}
            onChangeFileName={(e: React.ChangeEvent<HTMLInputElement>) =>
              ctx.setFieldValue('InvoiceName', e.target.value)
            }
            valueFileName={ctx.values.InvoiceName}
            maxMBSize={4}
            validExtensions={['jpeg', 'png', 'pdf']}
            base64
            {...fieldData}
          />
        );
      },
    },
  ];
}

export function useElinxSettingsRows(
  isReview: boolean,
  defaultNotificationMethod: ElinxNotificationMethod,
  selectedContact: Contact | null,
  country: string,
): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.ELinxSettings' });
  const dialog = useDialog();
  const [newEmails, setNewEmails] = useState<string[]>([]);

  return [
    {
      label: () => t('SendNotification.label', { defaultValue: 'Notifications' }),
      fieldName: 'SendNotification',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.SendNotification,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});

        return (
          <CardCheckbox
            indicator="switch"
            checked={fieldData.value}
            label={t('SendNotification.placeholder', { defaultValue: 'Send Recipient Notifications' })}
            {...fieldData}
          />
        );
      },
    },
    {
      label: () => t('SendReminder.label', { defaultValue: 'Reminders' }),
      fieldName: 'SendReminder',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.SendReminder,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});

        return (
          <CardCheckbox
            indicator="switch"
            checked={fieldData.value}
            label={t('SendReminder.checkboxLabel', { defaultValue: 'Send Recipient Reminders' })}
            {...fieldData}
          />
        );
      },
    },
    {
      label: () => t('ReminderFrequency.label', { defaultValue: 'Reminder Frequency (Days)' }),
      fieldName: 'ReminderFrequency',
      getInputState: (ctx) => (ctx.values.SendReminder ? 'required' : 'disabled'),
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.ReminderFrequency,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: (ctx) => (ctx.values.ReminderFrequency ? Yup.string().required() : Yup.string()),
    },
    {
      label: () => t('NotificationMethod.label', { defaultValue: 'Send eLinx Notification via' }),
      fieldName: 'NotificationMethod',
      getInputState: (ctx) => (ctx.values.SendNotification ? 'optional' : 'disabled'),
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.NotificationMethod,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        const options = {
          email: t('NotificationMethod.Options.email', { defaultValue: 'Email' }),
          sms: t('NotificationMethod.Options.sms', { defaultValue: 'SMS' }),
          ...(defaultNotificationMethod === 'whatsapp' && {
            whatsapp: t('NotificationMethod.Options.whatsapp', { defaultValue: 'WhatsApp' }),
          }),
        };

        return (
          <Radio variant="outlineShadow" readOnly={isReview || fieldData.disabled} options={options} {...fieldData} />
        );
      },
    },
    {
      label: () => t('ReceiverPhoneNumber.label', { defaultValue: 'Phone Number' }),
      fieldName: 'ReceiverPhoneNumber',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) =>
        !values.PaymentMethodDisplayOptions?.includes('Existing') &&
        values.NotificationMethod === 'sms' &&
        !!values.SendNotification &&
        !(!!values.PaymentSelectionMethod && values.PaymentSelectionMethod.includes('bank') && country === 'US'),
      serializeField: (values: ElinxInfo) => values.ReceiverPhoneNumber,
      tooltip: () => (
        <Tooltip
          information={t('ReceiverPhoneNumber.tooltip', {
            defaultValue:
              'Changes to the will apply only to this transaction and will not be saved to the contact profile',
          })}
          position="top"
          width="298px"
        />
      ),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('AdditionalRecipientEmails.label', { defaultValue: 'Additional Recipients Email' }),
      fieldName: 'AdditionalRecipientEmails',
      getInputState: () => 'optional',
      showField: (ctx) => !ctx.PaymentMethodDisplayOptions?.includes('Existing'),
      tooltip: () => (
        <Tooltip
          information={t('AdditionalRecipientEmails.tooltip', {
            defaultValue:
              'This is an additional email address. Any address added here will also receive all notifications and reminders.',
          })}
          position="top"
          width="298px"
        />
      ),
      renderField: ({ getFormikData, ctx }) => {
        const savedAdditionalEmails = selectedContact?.additionalContactEmails || [];
        const newAdditionalEmails = Array.from(new Set([...savedAdditionalEmails, ...newEmails]));

        const recipientEmails: string[] = Array.from(
          new Set([
            ...savedAdditionalEmails,
            ...ctx.values.AdditionalRecipientEmails,
            ...(ctx.values.OverrideEmailAddress ? [ctx.values.OverrideEmailAddress] : []),
          ]),
        );

        // do not show OverrideEmailAddress in Additional Recipients, but still check for duplicates.
        const visibleEmails = recipientEmails.filter(
          (email) => email.trim() && email !== ctx.values.OverrideEmailAddress,
        );

        const canAddEmail = (email: string) => {
          if (!email) return false;
          if (!Yup.string().email().required().isValidSync(email)) return false;
          if (recipientEmails.includes(email)) return false;

          return true;
        };

        const options = newAdditionalEmails.map((email) => ({
          value: email,
          label: email,
        }));

        const multiSelectFieldData = getFormikData<string[]>({
          formatValue: (value) => (Array.isArray(value) ? value : []),
          onChange: (e) => {
            const value = e.target.value;
            ctx.setFieldValue('AdditionalRecipientEmails', value === '' ? [] : value.split(','));
          },
        });

        async function handleAddEmail() {
          const newEmail = await dialog.open<string>(
            ({ onConfirm, onCancel }) => (
              <AddEmailDialog emails={newAdditionalEmails} onClose={onCancel} onAdd={onConfirm} />
            ),
            { bare: true },
          );

          if (newEmail) {
            setNewEmails((prev) => [...prev, newEmail]);
            ctx.setFieldValue('AdditionalRecipientEmails', [...ctx.values.AdditionalRecipientEmails, newEmail]);
          }
        }

        if (savedAdditionalEmails.length === 0)
          return (
            <EmailField
              emails={visibleEmails}
              onAdd={(newEmail) => {
                if (!canAddEmail(newEmail)) return;
                ctx.setFieldValue('AdditionalRecipientEmails', [...ctx.values.AdditionalRecipientEmails, newEmail]);
              }}
              onRemove={(email) => {
                ctx.setFieldValue(
                  'AdditionalRecipientEmails',
                  ctx.values.AdditionalRecipientEmails.filter((m) => m !== email),
                );
              }}
              {...getFormikData<string>({})}
            />
          );

        return (
          <>
            <MultiSelect
              placeholder={t('AdditionalRecipientEmails.selectPlaceholder', { defaultValue: 'Select Email Address' })}
              renderSelectedValue={(_, { Placeholder }) => <Placeholder />}
              options={options}
              renderOption={(o, { Option, SelectedIcon, selected }) => (
                <Option>
                  {o.label}
                  {selected && <SelectedIcon />}
                </Option>
              )}
              renderDropdownFooter={({ close }) => {
                return (
                  <Button
                    iconLeft="add"
                    size="small"
                    className={classes('addEmailButton')}
                    onClick={() => {
                      close();
                      handleAddEmail();
                    }}
                  >
                    {t('AdditionalRecipientEmails.addButton', { defaultValue: 'Additional Email Address' })}
                  </Button>
                );
              }}
              {...multiSelectFieldData}
            />

            {!!ctx.values.AdditionalRecipientEmails.length && (
              <div className={classes('multiselectOptionsContainer')}>
                {ctx.values.AdditionalRecipientEmails.map((email) => (
                  <div key={email} className={classes('multiselectOption')}>
                    <span>{email}</span>

                    <div
                      className={classes('item', isReview && 'disabled')}
                      onClick={() => {
                        const filteredList = ctx.values.AdditionalRecipientEmails.filter((e) => {
                          return email !== e;
                        });

                        ctx.setFieldValue('AdditionalRecipientEmails', filteredList);
                      }}
                    >
                      <MaterialSymbol icon="close" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        );
      },
      validate: () =>
        Yup.array().test(
          'is-valid-emails',
          t('AdditionalRecipientEmails.validation', {
            defaultValue: 'Please enter a valid comma-separated list of email(s).',
          }),
          (value?: string[]) => {
            if (!value || value.length === 0) return true;

            return value.every((email) => Yup.string().email().isValidSync(email.trim()));
          },
        ),
    },
  ];
}

function isToday(dateStr: string): boolean {
  const date = startOfDay(new Date(Number(dateStr)));
  const today = startOfDay(new Date());

  return isEqual(date, today);
}

export const mapToPaymentSelectionValue = (paymentMethod: PaymentMethodType): string => {
  const mapping: Record<PaymentMethodType, string> = {
    BankAccount: 'bank',
    CreditCard: 'credit',
    DebitCard: 'debitcard',
    PhoneNumber: 'email',
    Email: 'email',
    GooglePay: 'googlepay',
    ApplePay: 'applepay',
    PayPal: 'paypal',
    Venmo: 'venmo',
    Zelle: 'zelle',
  };

  return mapping[paymentMethod] || '';
};

// Helper function to compute default PaymentSelectionMethod values
export const getDefaultPaymentSelectionMethods = (
  formType: 'Pay' | 'Collect' | 'Connect' | 'Flex',
  country: string,
  lockedSelectOptions: Record<string, string> | undefined,
  scheduleStartDate?: string,
  frequency?: string,
  paymentMethods?: PaymentMethod[],
  paymentMethodDisplayOptions?: string,
  // Currency-dependent availability (see usePaymentMethodCurrencySupport); omitted = every method qualifies.
  isMethodAvailable: (value: string) => boolean = () => true,
): string => {
  type PaymentOption = {
    value: string;
    featureKey: string;
    visible: boolean;
  };

  const options: PaymentOption[] = [
    {
      value: 'bank',
      featureKey: 'Bank Account',
      visible: true,
    },
    {
      value: 'email',
      featureKey: formType === 'Pay' ? 'Interac e-Transfer' : 'Interac e-Transfer Money Request',
      visible: country === 'CA' && formType !== 'Flex' && formType !== 'Connect',
    },
    {
      value: 'email',
      featureKey: 'Email',
      visible: country === 'CA' && formType === 'Connect',
    },
    {
      value: 'credit',
      featureKey: 'Credit Card',
      visible: formType !== 'Pay',
    },
    {
      value: 'debitcard',
      featureKey: 'Debit Card',
      visible: formType === 'Pay' || formType === 'Connect',
    },
    {
      value: 'googlepay',
      featureKey: 'Google Pay',
      visible: formType === 'Collect' || formType === 'Flex',
    },
    {
      value: 'applepay',
      featureKey: 'Apple Pay',
      visible: formType === 'Collect' || formType === 'Flex',
    },
    {
      value: 'paypal',
      featureKey: 'PayPal',
      visible: formType === 'Pay' || formType === 'Connect',
    },
    {
      value: 'venmo',
      featureKey: 'Venmo',
      visible: (formType === 'Pay' || formType === 'Connect') && country === 'US',
    },
    {
      value: 'zelle',
      featureKey: 'Zelle',
      visible: (formType === 'Pay' || formType === 'Connect') && country === 'US',
    },
  ];

  const isTodaySchedule = scheduleStartDate ? isToday(scheduleStartDate) : true;
  const isSingleFrequency = frequency === 'single' || !frequency;

  // Filter based on visibility, locked status, and scheduling constraints
  let availableOptions = options.filter((option) => {
    if (!option.visible || !isMethodAvailable(option.value)) return false;
    // Check if locked via LockedSelectOptions
    if (lockedSelectOptions && option.featureKey in lockedSelectOptions) return false;
    // If not today or not single frequency, only allow bank
    if (!(isTodaySchedule && isSingleFrequency) && option.value !== 'bank') return false;

    return true;
  });

  // If PaymentMethodDisplayOptions is "Existing", filter by available payment methods
  if (paymentMethodDisplayOptions === 'Existing' && paymentMethods) {
    const existingPaymentMethodValues = new Set(
      paymentMethods.map((pm) => mapToPaymentSelectionValue(pm.PaymentMethod)),
    );

    availableOptions = availableOptions.filter((option) => existingPaymentMethodValues.has(option.value));
  }

  // Remove duplicates and join
  const uniqueValues = [...new Set(availableOptions.map((opt) => opt.value))];

  return uniqueValues.join(',');
};

export const getDefaultBankingPaymentRails = (
  formType: 'Pay' | 'Collect' | 'Connect' | 'Flex',
  country: string,
  lockedSelectOptions: Record<string, string> | undefined,
  scheduleStartDate?: string,
  frequency?: string,
): string => {
  type BankingRailOption = {
    value: string;
    featureKey: string;
    visible: boolean;
    isRecurringEnabled: boolean;
  };

  const options: BankingRailOption[] = [
    {
      value: 'eft',
      featureKey: 'eft',
      visible: country === 'CA',
      isRecurringEnabled: true,
    },
    {
      value: 'vopayinstant',
      featureKey: 'VoPay Instant',
      visible: country === 'CA' && formType === 'Pay',
      isRecurringEnabled: false,
    },
    {
      value: 'ach',
      featureKey: 'USD',
      visible: country === 'US',
      isRecurringEnabled: true,
    },
    {
      value: 'rtp',
      featureKey: 'RTP',
      visible: country === 'US' && formType === 'Pay',
      isRecurringEnabled: false,
    },
    {
      value: 'fedwire',
      featureKey: 'Wire',
      visible: country === 'US' && formType === 'Pay',
      isRecurringEnabled: false,
    },
  ];

  const isTodaySchedule = scheduleStartDate ? isToday(scheduleStartDate) : true;
  const isSingleFrequency = frequency === 'single' || !frequency;

  const availableOptions = options.filter((option) => {
    if (!option.visible) return false;
    // Check if locked via LockedSelectOptions
    if (lockedSelectOptions && option.featureKey in lockedSelectOptions) return false;
    // If not today or not single frequency, only allow recurring-enabled rails
    if (!(isTodaySchedule && isSingleFrequency) && !option.isRecurringEnabled) return false;

    return true;
  });

  return availableOptions.map((opt) => opt.value).join(',');
};
